/**
 * BLRRD Editor — IndexedDB schema and migration.
 *
 * Database: `blrrd`, version 1. Object stores per Phase 1.D plan:
 *   files     — uploaded source images (Blob), keyed by content hash
 *   projects  — full editor state snapshots, keyed by uuid
 *   history   — undo-stack overflow (capped at 100 entries)
 *   presets   — user "Looks" (succeeds the localStorage path long-term)
 *   luts      — imported .cube LUT files (Phase 1.5 territory)
 *
 * v1 just creates stores + indexes. No data migration logic is needed yet
 * because this is the genesis schema. When v2 lands, branch on oldVersion
 * inside `onupgradeneeded`.
 *
 * No consumer is wired in 1.D — the editor still uses localStorage for
 * snapshots/looks. We're shipping the schema + open helper so 1.E/1.F can
 * migrate gradually, and `scripts/test-idb.mjs` enforces the schema doesn't
 * regress.
 */

export const DB_NAME    = 'blrrd';
export const DB_VERSION = 2;

export const STORES = Object.freeze({
    files:    { keyPath: 'hash',  indexes: [
        { name: 'createdAt', keyPath: 'createdAt', unique: false },
    ] },
    projects: { keyPath: 'id',    indexes: [
        { name: 'updatedAt', keyPath: 'updatedAt', unique: false },
        /* v2: cascade-delete + listProjectsForFile both query by fileHash. */
        { name: 'fileHash',  keyPath: 'fileHash',  unique: false },
    ] },
    history:  { keyPath: 'id', autoIncrement: true,
                indexes: [{ name: 'projectId', keyPath: 'projectId', unique: false }] },
    presets:  { keyPath: 'name',  indexes: [{ name: 'createdAt', keyPath: 'createdAt', unique: false }] },
    luts:     { keyPath: 'name',  indexes: [{ name: 'size', keyPath: 'size', unique: false }] },
});

export const HISTORY_MAX = 100;

function applySchema(db, oldVersion, transaction) {
    /* v1 — initial schema. */
    if (oldVersion < 1) {
        for (const [name, def] of Object.entries(STORES)) {
            if (db.objectStoreNames.contains(name)) continue;
            const store = db.createObjectStore(name, {
                keyPath:       def.keyPath,
                autoIncrement: def.autoIncrement === true,
            });
            (def.indexes || []).forEach((ix) => {
                store.createIndex(ix.name, ix.keyPath, { unique: ix.unique === true });
            });
        }
    }
    /* v2 — Phase 2.D file basics: projects.fileHash index for cascade
     * cleanup + listProjectsForFile without full table scan. */
    if (oldVersion < 2) {
        if (db.objectStoreNames.contains('projects') && transaction) {
            const projectsStore = transaction.objectStore('projects');
            const existing = Array.from(projectsStore.indexNames);
            if (!existing.includes('fileHash')) {
                projectsStore.createIndex('fileHash', 'fileHash', { unique: false });
            }
        }
    }
}

/* Promise-wrapped IDBOpenDBRequest. Errors bubble; callers decide whether
 * a missing IDB is fatal (they're not — editor falls back to localStorage). */
export function openDB() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB not available'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (event) => applySchema(req.result, event.oldVersion || 0, req.transaction);
        req.onsuccess = () => resolve(req.result);
        req.onerror   = () => reject(req.error || new Error('IndexedDB open failed'));
        req.onblocked = () => reject(new Error('IndexedDB open blocked by other tab'));
    });
}

/* Convenience: open + return diagnostic shape. Used by the IDB CI test
 * and by storage-quota UI. */
export async function describeDB() {
    const db = await openDB();
    try {
        const stores = Array.from(db.objectStoreNames);
        const detail = {};
        for (const name of stores) {
            const def = STORES[name];
            detail[name] = {
                keyPath:       def?.keyPath || null,
                autoIncrement: def?.autoIncrement === true,
                indexes:       (def?.indexes || []).map((ix) => ix.name),
            };
        }
        return { name: DB_NAME, version: db.version, stores, detail };
    } finally {
        db.close();
    }
}
